import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { CATEGORIES, type Category } from "@/lib/categories";
import { Loader2, Gavel, Clock, CheckCircle2, Inbox } from "lucide-react";

export const Route = createFileRoute("/accountability")({
  component: AccountabilityPage,
  head: () => ({
    meta: [
      { title: "Xamseen — Civic Accountability Scoreboard" },
      { name: "description", content: "See how quickly authorities respond to and resolve reported problems — response times, resolution rates and open backlogs by agency and category." },
      { property: "og:title", content: "Xamseen — Civic Accountability Scoreboard" },
      { property: "og:description", content: "Public response times, resolution rates and open backlogs for reported civic problems." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
});

type PostRow = { id: string; category: string; status: string; created_at: string; updated_at: string };
type ResponseRow = { post_id: string; agency: string | null; created_at: string; resolves: boolean };

const RESOLVED = new Set(["resolved", "closed"]);

function median(nums: number[]) {
  if (!nums.length) return null;
  const s = [...nums].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}

function days(from: string, to: string) {
  return (new Date(to).getTime() - new Date(from).getTime()) / 86400000;
}

function fmtDays(d: number | null) {
  if (d === null) return "—";
  if (d < 1) return `${Math.max(Math.round(d * 24), 1)}h`;
  return `${d.toFixed(1)}d`;
}

function AccountabilityPage() {
  const [posts, setPosts] = useState<PostRow[]>([]);
  const [responses, setResponses] = useState<ResponseRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const since = new Date(Date.now() - 180 * 86400000).toISOString();
    Promise.all([
      supabase
        .from("posts")
        .select("id, category, status, created_at, updated_at")
        .in("post_type", ["issue", "emergency"] as never)
        .gte("created_at", since)
        .limit(2000),
      supabase
        .from("official_responses")
        .select("post_id, agency, created_at, resolves")
        .gte("created_at", since)
        .limit(2000),
    ])
      .then(([p, r]) => {
        setPosts((p.data ?? []) as PostRow[]);
        setResponses((r.data ?? []) as ResponseRow[]);
      })
      .finally(() => setLoading(false));
  }, []);

  const postMap = useMemo(() => new Map(posts.map((p) => [p.id, p])), [posts]);

  const firstResponse = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of responses) {
      const prev = m.get(r.post_id);
      if (!prev || r.created_at < prev) m.set(r.post_id, r.created_at);
    }
    return m;
  }, [responses]);

  const totals = useMemo(() => {
    const resolved = posts.filter((p) => RESOLVED.has(p.status));
    const responseDays = [...firstResponse.entries()]
      .map(([id, at]) => { const p = postMap.get(id); return p ? days(p.created_at, at) : null; })
      .filter((v): v is number => v !== null && v >= 0);
    const resolutionDays = resolved.map((p) => days(p.created_at, p.updated_at)).filter((v) => v >= 0);
    return {
      reports: posts.length,
      answered: firstResponse.size,
      resolved: resolved.length,
      open: posts.length - resolved.length,
      medianResponse: median(responseDays),
      medianResolution: median(resolutionDays),
    };
  }, [posts, firstResponse, postMap]);

  const byAgency = useMemo(() => {
    const m = new Map<string, { responses: number; resolves: number; responseDays: number[] }>();
    for (const r of responses) {
      const key = (r.agency ?? "").trim() || "Unattributed authority";
      const row = m.get(key) ?? { responses: 0, resolves: 0, responseDays: [] };
      row.responses += 1;
      if (r.resolves) row.resolves += 1;
      const p = postMap.get(r.post_id);
      if (p) { const d = days(p.created_at, r.created_at); if (d >= 0) row.responseDays.push(d); }
      m.set(key, row);
    }
    return [...m.entries()]
      .map(([agency, v]) => ({ agency, responses: v.responses, resolves: v.resolves, medianResponse: median(v.responseDays) }))
      .sort((a, b) => b.responses - a.responses)
      .slice(0, 12);
  }, [responses, postMap]);

  const byCategory = useMemo(() => {
    const m = new Map<string, { total: number; resolved: number; answered: number; resolutionDays: number[] }>();
    for (const p of posts) {
      const row = m.get(p.category) ?? { total: 0, resolved: 0, answered: 0, resolutionDays: [] };
      row.total += 1;
      if (RESOLVED.has(p.status)) { row.resolved += 1; const d = days(p.created_at, p.updated_at); if (d >= 0) row.resolutionDays.push(d); }
      if (firstResponse.has(p.id)) row.answered += 1;
      m.set(p.category, row);
    }
    return [...m.entries()]
      .map(([category, v]) => ({
        category,
        total: v.total,
        resolved: v.resolved,
        answered: v.answered,
        rate: v.total ? Math.round((v.resolved / v.total) * 100) : 0,
        medianResolution: median(v.resolutionDays),
      }))
      .sort((a, b) => b.total - a.total);
  }, [posts, firstResponse]);

  return (
    <AppShell>
      <div className="mb-5">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Accountability scoreboard</h1>
        <p className="text-sm text-muted-foreground">
          How reported problems are being answered and resolved over the last 180 days. Public by design.
        </p>
      </div>

      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[
              { label: "Reports", value: totals.reports, sub: `${totals.answered} answered by an authority`, icon: Inbox },
              { label: "Resolved", value: totals.resolved, sub: `${totals.reports ? Math.round((totals.resolved / totals.reports) * 100) : 0}% resolution rate`, icon: CheckCircle2 },
              { label: "Median first response", value: fmtDays(totals.medianResponse), sub: "from report to official reply", icon: Clock },
              { label: "Median time to resolve", value: fmtDays(totals.medianResolution), sub: `${totals.open} still open`, icon: Gavel },
            ].map((s) => (
              <Card key={s.label} className="p-4">
                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                  <s.icon className="h-3.5 w-3.5" />{s.label}
                </div>
                <div className="mt-2 font-display text-2xl font-semibold tabular-nums">{s.value}</div>
                <p className="mt-1 text-xs text-muted-foreground">{s.sub}</p>
              </Card>
            ))}
          </div>

          <Card className="p-5">
            <h2 className="font-display text-base font-semibold">Authorities responding</h2>
            <p className="mt-1 text-sm text-muted-foreground">Agencies ranked by official responses posted on reports.</p>
            {byAgency.length === 0 ? (
              <p className="mt-4 text-sm text-muted-foreground">No official responses recorded yet.</p>
            ) : (
              <ul className="mt-4 divide-y divide-border">
                {byAgency.map((a) => (
                  <li key={a.agency} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3 text-sm">
                    <span className="min-w-0 flex-1 truncate font-medium">{a.agency}</span>
                    <span className="tabular-nums text-muted-foreground">{a.responses} responses</span>
                    <Badge variant="outline" className="font-normal">{a.resolves} marked resolved</Badge>
                    <span className="tabular-nums text-muted-foreground">{fmtDays(a.medianResponse)} median reply</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-5">
            <h2 className="font-display text-base font-semibold">By category</h2>
            <p className="mt-1 text-sm text-muted-foreground">Where the system is working — and where reports stall.</p>
            <ul className="mt-4 space-y-3">
              {byCategory.map((c) => {
                const meta = CATEGORIES[c.category as Category];
                return (
                  <li key={c.category} className="space-y-1.5">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                      {meta && (
                        <span className="rounded-full p-1.5" style={{ background: meta.color + "15" }}>
                          <meta.icon className="h-3.5 w-3.5" style={{ color: meta.color }} />
                        </span>
                      )}
                      <span className="font-medium">{meta?.label ?? c.category}</span>
                      <span className="tabular-nums text-muted-foreground">{c.total} reports</span>
                      <span className="tabular-nums text-muted-foreground">{c.answered} answered</span>
                      <span className="ml-auto tabular-nums text-muted-foreground">{fmtDays(c.medianResolution)} to resolve</span>
                      <span className="tabular-nums font-medium">{c.rate}%</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                      <div className="h-full rounded-full bg-primary" style={{ width: `${c.rate}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </Card>

          <p className="text-xs text-muted-foreground">
            Something missing from this scoreboard? <Link to="/report" className="text-primary hover:underline">Report a problem</Link> and it will be tracked here.
          </p>
        </div>
      )}
    </AppShell>
  );
}
